/**
 * Social Videos Section - Interactive JavaScript
 * Swiper v11 + Reference code logic from Beatbot
 * Fully rewritten to match reference functionality with latest Swiper API
 */

(function() {
  'use strict';

  class SocialVideosSection {
    constructor(sectionElement) {
      this.section = sectionElement;
      this.sectionId = this.section.dataset.sectionId;
      this.tabs = this.section.querySelectorAll('.social-videos__tab');
      this.tabContents = this.section.querySelectorAll('.social-videos__tab-content');
      this.swiperInstances = {
        instagram: null,
        youtube: null
      };
      this.sectionObserver = null;

      // Settings from section
      this.autoplayOnViewport = this.section.dataset.autoplay === 'true';
      this.enableHoverPlay = this.section.dataset.hoverPlay === 'true';
      this.muteVideos = this.section.dataset.mute === 'true';

      this.init();
    }

    init() {
      this.setupTabs();
      this.initializeTabIndicator();
      this.setupVideoCards();
      this.setupModal();

      // Delay Swiper initialization until section is near viewport
      if ('IntersectionObserver' in window) {
        const initObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.initializeSwipers();
              initObserver.disconnect();
            }
          });
        }, {
          rootMargin: '200px'
        });

        initObserver.observe(this.section);
      } else {
        // Fallback: initialize immediately
        this.initializeSwipers();
      }

      if (this.autoplayOnViewport) {
        this.setupSectionObserver();
      }

      // Handle window resize
      this.setupResizeHandler();
    }

    initializeSwipers() {
      // Initialize swiper for active tab
      const activeTab = this.section.querySelector('.social-videos__tab.active');
      if (activeTab) {
        this.initSwiper(activeTab.getAttribute('data-tab'));
      } else {
        // Fallback: initialize based on active content
        const instagramContent = this.section.querySelector('#instagram-content-' + this.sectionId);
        const youtubeContent = this.section.querySelector('#youtube-content-' + this.sectionId);

        if (instagramContent && instagramContent.classList.contains('active')) {
          this.initSwiper('instagram');
        } else if (youtubeContent && youtubeContent.classList.contains('active')) {
          this.initSwiper('youtube');
        }
      }
    }

    /**
     * Tab Switching Logic
     */
    setupTabs() {
      if (this.tabs.length <= 1) return;

      this.tabs.forEach(tab => {
        tab.addEventListener('click', () => this.switchTab(tab));
      });
    }

    switchTab(targetTab) {
      const targetTabId = targetTab.dataset.tab;
      const currentActiveContent = this.section.querySelector('.social-videos__tab-content.active');
      const targetContent = this.section.querySelector(`#${targetTabId}-content-${this.sectionId}`);

      if (!targetContent || targetContent === currentActiveContent) return;

      // Update tabs
      this.tabs.forEach(t => t.classList.remove('active'));
      targetTab.classList.add('active');

      // Update indicator
      this.updateTabIndicator(targetTab);

      // Pause all videos in current tab
      if (currentActiveContent) {
        this.pauseAllVideosInContainer(currentActiveContent);
        currentActiveContent.classList.add('fade-out');

        setTimeout(() => {
          currentActiveContent.classList.remove('active', 'fade-out');
        }, 300);
      }

      // Show new tab
      setTimeout(() => {
        targetContent.classList.add('active', 'fade-in');

        // Initialize or update swiper
        setTimeout(() => this.initSwiper(targetTabId), 50);

        setTimeout(() => {
          targetContent.classList.remove('fade-in');
        }, 500);
      }, 300);
    }

    updateTabIndicator(activeTab) {
      const tabsContainer = this.section.querySelector('.social-videos__tabs');
      if (!tabsContainer || !activeTab) return;

      const tabRect = activeTab.getBoundingClientRect();
      const tabsRect = tabsContainer.getBoundingClientRect();

      const offsetLeft = tabRect.left - tabsRect.left;
      const tabWidth = tabRect.width;

      tabsContainer.style.setProperty('--indicator-left', `${offsetLeft}px`);
      tabsContainer.style.setProperty('--indicator-width', `${tabWidth}px`);
      tabsContainer.style.setProperty('--indicator-opacity', '1');
    }

    initializeTabIndicator() {
      const activeTab = this.section.querySelector('.social-videos__tab.active');
      if (!activeTab) return;

      const tabsContainer = this.section.querySelector('.social-videos__tabs');
      if (tabsContainer) {
        tabsContainer.style.transition = 'none';
        this.updateTabIndicator(activeTab);
        requestAnimationFrame(() => {
          tabsContainer.style.transition = '';
        });
      }
    }

    setupResizeHandler() {
      let resizeTimeout;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          const activeTab = this.section.querySelector('.social-videos__tab.active');
          if (activeTab) {
            this.updateTabIndicator(activeTab);
          }
          // Re-check alignment for both Instagram and YouTube
          this.checkInstagramAlignment();
          this.checkYouTubeAlignment();
        }, 150);
      });
    }

    /**
     * Instagram Alignment Check - Matches reference code logic
     */
    checkInstagramAlignment() {
      const instagramWrapper = this.section.querySelector(`#instagram-content-${this.sectionId} .swiper-wrapper`);
      const instagramContainer = this.section.querySelector(`#instagram-content-${this.sectionId} .instagram-swiper-container`);

      if (!instagramWrapper || !instagramContainer) return;

      const slides = instagramWrapper.querySelectorAll('.swiper-slide');
      if (slides.length === 0) return;

      // Calculate total width of all slides
      let totalSlidesWidth = 0;
      slides.forEach(slide => {
        const slideWidth = slide.offsetWidth;
        const slideStyle = window.getComputedStyle(slide);
        const marginLeft = parseFloat(slideStyle.marginLeft) || 0;
        const marginRight = parseFloat(slideStyle.marginRight) || 0;
        totalSlidesWidth += slideWidth + marginLeft + marginRight;
      });

      // Add space between slides
      const spaceBetween = 10;
      totalSlidesWidth += (slides.length - 1) * spaceBetween;

      const containerWidth = instagramContainer.offsetWidth;

      // If all slides fit, center align
      if (totalSlidesWidth < containerWidth) {
        instagramWrapper.classList.add('center-align');
      } else {
        instagramWrapper.classList.remove('center-align');
      }
    }

    /**
     * YouTube Alignment Check - Same logic as Instagram
     */
    checkYouTubeAlignment() {
      const youtubeWrapper = this.section.querySelector(`#youtube-content-${this.sectionId} .swiper-wrapper`);
      const youtubeContainer = this.section.querySelector(`#youtube-content-${this.sectionId} .youtube-swiper-container`);

      if (!youtubeWrapper || !youtubeContainer) return;

      const slides = youtubeWrapper.querySelectorAll('.swiper-slide');
      if (slides.length === 0) return;

      // Calculate total width of all slides
      let totalSlidesWidth = 0;
      slides.forEach(slide => {
        const slideWidth = slide.offsetWidth;
        const slideStyle = window.getComputedStyle(slide);
        const marginLeft = parseFloat(slideStyle.marginLeft) || 0;
        const marginRight = parseFloat(slideStyle.marginRight) || 0;
        totalSlidesWidth += slideWidth + marginLeft + marginRight;
      });

      // Add space between slides
      const spaceBetween = 10;
      totalSlidesWidth += (slides.length - 1) * spaceBetween;

      const containerWidth = youtubeContainer.offsetWidth;

      // If all slides fit, center align
      if (totalSlidesWidth < containerWidth) {
        youtubeWrapper.classList.add('center-align');
      } else {
        youtubeWrapper.classList.remove('center-align');
      }
    }

    /**
     * Initialize Swiper Carousels - Swiper v11 API
     */
    initSwiper(tabId) {
      const isDesktop = window.innerWidth > 768;

      if (tabId === 'instagram') {
        const instagramContainer = this.section.querySelector(`#instagram-content-${this.sectionId} .instagram-swiper-container`);
        if (!instagramContainer) return;

        if (!this.swiperInstances.instagram) {
          const instagramCount = parseInt(this.section.querySelector('#instagram-content-' + this.sectionId)?.dataset.videoCount || 0);

          this.swiperInstances.instagram = new Swiper(instagramContainer, {
            slidesPerView: 'auto',
            spaceBetween: 10,
            loop: false,
            grabCursor: true,
            centeredSlides: false,
            resistanceRatio: 0,
            pagination: {
              el: `.instagram-swiper-pagination`,
              clickable: true,
            },
            navigation: {
              nextEl: `.instagram-swiper-next`,
              prevEl: `.instagram-swiper-prev`,
            },
            on: {
              init: (swiper) => {
                this.handleSwiperInit(swiper, 'instagram', instagramCount);
                setTimeout(() => this.checkInstagramAlignment(), 100);
              },
              slideChangeTransitionStart: (swiper) => {
                this.pauseAllVideosInContainer(instagramContainer);
              },
              slideChangeTransitionEnd: (swiper) => {
                // Auto-play active slide video (reference code logic)
                const activeSlide = swiper.slides[swiper.activeIndex];
                if (activeSlide) {
                  const activeVideo = activeSlide.querySelector('.social-video-card video');
                  const activeCard = activeSlide.querySelector('.social-video-card');
                  if (activeVideo && activeCard && activeCard.classList.contains('instagram-card')) {
                    // Load video if not loaded
                    if (activeVideo.hasAttribute('data-src') && !activeVideo.src) {
                      activeVideo.src = activeVideo.getAttribute('data-src');
                      activeVideo.removeAttribute('data-src');
                    }
                    activeVideo.play().catch(e => console.error('Video play failed:', e));
                  }
                }
              }
            }
          });
        } else {
          this.swiperInstances.instagram.update();
          setTimeout(() => this.checkInstagramAlignment(), 100);
        }

      } else if (tabId === 'youtube') {
        const youtubeContainer = this.section.querySelector(`#youtube-content-${this.sectionId} .youtube-swiper-container`);
        if (!youtubeContainer) return;

        if (!this.swiperInstances.youtube) {
          const youtubeCount = parseInt(this.section.querySelector('#youtube-content-' + this.sectionId)?.dataset.videoCount || 0);

          this.swiperInstances.youtube = new Swiper(youtubeContainer, {
            slidesPerView: 'auto',
            spaceBetween: 10,
            loop: false,
            grabCursor: true,
            centeredSlides: false,
            resistanceRatio: 0,
            pagination: {
              el: `.youtube-swiper-pagination`,
              clickable: true,
            },
            navigation: {
              nextEl: `.youtube-swiper-next`,
              prevEl: `.youtube-swiper-prev`,
            },
            on: {
              init: (swiper) => {
                this.handleSwiperInit(swiper, 'youtube', youtubeCount);
                setTimeout(() => this.checkYouTubeAlignment(), 100);
              },
              slideChangeTransitionStart: (swiper) => {
                this.pauseAllVideosInContainer(youtubeContainer);
              }
            }
          });
        } else {
          this.swiperInstances.youtube.update();
          setTimeout(() => this.checkYouTubeAlignment(), 100);
        }
      }
    }

    /**
     * Handle Swiper Init - Calculate visible slides and hide controls if needed
     */
    handleSwiperInit(swiper, type, videoCount) {
      const isDesktop = window.innerWidth > 768;
      if (!isDesktop) return;

      const controls = this.section.querySelector(`.${type}-swiper-controls`);
      if (!controls) return;

      // Calculate visible slides
      let visibleSlides = 1;
      if (swiper.slides && swiper.slides.length > 0 && swiper.width > 0) {
        const slideWidth = swiper.slides[0].offsetWidth;
        const spaceBetween = swiper.params.spaceBetween || 0;
        visibleSlides = Math.floor(swiper.width / (slideWidth + spaceBetween));
      }

      visibleSlides = Math.max(1, visibleSlides || 1);

      // Hide controls if all videos fit in viewport
      if (videoCount <= visibleSlides) {
        controls.style.display = 'none';
      } else {
        controls.style.display = 'flex';
      }
    }

    /**
     * Video Card Interactions
     */
    setupVideoCards() {
      const videoCards = this.section.querySelectorAll('.social-video-card');

      videoCards.forEach(card => {
        const videoType = card.dataset.videoType;

        if (videoType === 'instagram') {
          this.setupInstagramCard(card);
        } else if (videoType === 'youtube') {
          this.setupYouTubeCard(card);
        }
      });
    }

    setupInstagramCard(card) {
      const video = card.querySelector('video');
      const playButton = card.querySelector('.social-video-card__direct-play-button');
      const fullscreenButton = card.querySelector('.social-video-card__fullscreen-play-button');

      if (!video || !playButton) return;

      // Check if video has poster attribute
      const hasPoster = video.hasAttribute('poster') && video.getAttribute('poster') !== '';

      // Lazy loading logic - reference code strategy
      let videoLoaded = false;
      let thumbnailLoaded = false;
      const isFirstVideo = card.closest('.swiper-slide')?.classList.contains('swiper-slide-active') ||
                          card.closest('.swiper-slide') === card.closest('.swiper-wrapper')?.firstElementChild;

      const loadVideoThumbnail = () => {
        if (!thumbnailLoaded && !hasPoster && video.hasAttribute('data-src')) {
          // 只加载缩略图,不是完整视频
          video.src = video.getAttribute('data-src');
          video.load(); // 触发加载元数据
          thumbnailLoaded = true;
          // 注意: 保留data-src,因为还没有完整加载
        }
      };

      const loadVideo = () => {
        if (!videoLoaded && video.hasAttribute('data-src')) {
          video.src = video.getAttribute('data-src');
          video.removeAttribute('data-src');
          videoLoaded = true;
          thumbnailLoaded = true;
        }
      };

      // 使用Intersection Observer延迟加载缩略图
      if (!hasPoster) {
        if ('IntersectionObserver' in window) {
          const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                loadVideoThumbnail();
                observer.disconnect();
              }
            });
          }, {
            rootMargin: '100px' // 提前100px开始加载
          });
          observer.observe(card);
        } else {
          // 降级: 立即加载
          loadVideoThumbnail();
        }
      }

      // 第一个视频立即完整加载
      if (isFirstVideo) {
        loadVideo();
      }

      // Toggle play/pause
      const togglePlay = (event) => {
        if (event) {
          event.preventDefault();
          event.stopPropagation();
        }

        // Skip if fullscreen button clicked
        if (event && event.target.closest('.social-video-card__fullscreen-play-button')) {
          return;
        }

        loadVideo();

        if (video.paused) {
          this.pauseAllVideosInContainer(card.closest('.social-videos__tab-content'));
          video.play().catch(err => console.error('Video play failed:', err));
          card.classList.add('playing');
        } else {
          video.pause();
          card.classList.remove('playing');
        }
      };

      card.addEventListener('click', togglePlay);

      // Hover to play (desktop only) - reference code behavior
      if (this.enableHoverPlay && window.innerWidth > 768) {
        card.addEventListener('mouseenter', () => {
          loadVideo();
          this.pauseAllVideosInContainer(card.closest('.social-videos__tab-content'));
          video.play().catch(err => console.error('Video play failed:', err));
          card.classList.add('playing');
        });

        card.addEventListener('mouseleave', () => {
          video.pause();
          card.classList.remove('playing');
        });
      }

      // Video events
      video.addEventListener('play', () => {
        playButton.classList.add('is-playing');
        card.classList.add('playing');
      });

      video.addEventListener('pause', () => {
        playButton.classList.remove('is-playing');
        card.classList.remove('playing');
      });

      video.addEventListener('ended', () => {
        playButton.classList.remove('is-playing');
        card.classList.remove('playing');
      });

      // Fullscreen button
      if (fullscreenButton) {
        fullscreenButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();

          // iOS device detection
          const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

          // iOS: Use native fullscreen, Others: Use custom modal
          if (isIOS && typeof video.webkitEnterFullscreen === 'function') {
            // iOS native fullscreen
            video.webkitEnterFullscreen();
          } else {
            // Non-iOS devices: Use current modal implementation
            this.openVideoModal(video);
          }
        });
      }
    }

    setupYouTubeCard(card) {
      const iframe = card.querySelector('.social-video-card__youtube-iframe');
      const youtubeId = card.dataset.youtubeId;

      if (!iframe || !youtubeId) return;

      // Lazy load iframe using Intersection Observer
      let iframeLoaded = false;
      const loadIframe = () => {
        if (!iframeLoaded && iframe.dataset.src) {
          iframe.src = iframe.dataset.src;
          iframe.removeAttribute('data-src');
          iframeLoaded = true;
        }
      };

      // Use Intersection Observer for lazy loading
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              loadIframe();
              observer.disconnect();
            }
          });
        }, {
          rootMargin: '50px'
        });

        observer.observe(card);
      } else {
        // Fallback: load on hover
        card.addEventListener('mouseenter', loadIframe, { once: true });
      }

      // Create clickable overlay - reference code approach
      const mediaContainer = card.querySelector('.social-video-card__media');
      if (!mediaContainer) return;

      // Check if overlay already exists
      if (!mediaContainer.querySelector('.social-video-card__youtube-overlay')) {
        const overlay = document.createElement('div');
        overlay.className = 'social-video-card__youtube-overlay';
        mediaContainer.appendChild(overlay);

        overlay.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();

          // Ensure iframe is loaded before opening modal
          loadIframe();

          this.openYouTubeModal(youtubeId);
        });
      }
    }

    /**
     * Auto-play first video when section enters viewport
     */
    setupSectionObserver() {
      if (!('IntersectionObserver' in window)) return;

      this.sectionObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.autoplayFirstVideo();
            this.sectionObserver.disconnect();
          }
        });
      }, {
        threshold: 0.3
      });

      this.sectionObserver.observe(this.section);
    }

    autoplayFirstVideo() {
      const activeTabContent = this.section.querySelector('.social-videos__tab-content.active');
      if (!activeTabContent) return;

      const firstCard = activeTabContent.querySelector('.social-video-card.instagram-card');
      if (!firstCard) return;

      const firstVideo = firstCard.querySelector('video');
      if (!firstVideo) return;

      // Load video source
      if (firstVideo.hasAttribute('data-src') && !firstVideo.src) {
        firstVideo.src = firstVideo.getAttribute('data-src');
        firstVideo.removeAttribute('data-src');
      }

      setTimeout(() => {
        firstVideo.play().catch(err => console.error('Auto-play failed:', err));
      }, 100);
    }

    /**
     * Modal Functionality
     */
    setupModal() {
      const modal = document.getElementById(`social-videos-modal-${this.sectionId}`);
      if (!modal) return;

      const closeButton = modal.querySelector('.social-videos-modal__close');
      const modalVideo = modal.querySelector('.social-videos-modal__video');

      // Close button
      if (closeButton) {
        closeButton.addEventListener('click', () => this.closeModal());
      }

      // Click outside to close
      modal.addEventListener('click', (event) => {
        if (event.target === modal) {
          this.closeModal();
        }
      });

      // Escape key to close
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && modal.classList.contains('active')) {
          this.closeModal();
        }
      });
    }

    openVideoModal(video) {
      const modal = document.getElementById(`social-videos-modal-${this.sectionId}`);
      const modalVideo = document.getElementById(`social-videos-modal-video-${this.sectionId}`);

      if (!modal || !modalVideo || !video) return;

      modalVideo.src = video.src || video.dataset.src;
      modalVideo.currentTime = video.currentTime;
      modal.classList.add('active');
      modalVideo.play().catch(err => console.error('Modal video play failed:', err));

      // Pause original video
      video.pause();

      // Prevent body scroll
      document.body.style.overflow = 'hidden';
    }

    openYouTubeModal(youtubeId) {
      if (!youtubeId) return;

      // Dynamic modal creation - reference code approach
      const modal = document.createElement('div');
      modal.className = 'youtube-fullscreen-modal';
      modal.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: rgba(0, 0, 0, 0.95);
        z-index: 999999;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        transition: opacity 0.3s ease;
      `;

      // Create iframe
      const iframe = document.createElement('iframe');
      iframe.src = `https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0&modestbranding=1&fs=1&iv_load_policy=3`;
      iframe.style.cssText = `
        width: 90vw;
        height: 50.625vw;
        max-height: 90vh;
        border: none;
        border-radius: 8px;
      `;
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen';
      iframe.allowFullscreen = true;

      // Create close button
      const closeButton = document.createElement('button');
      closeButton.innerHTML = '×';
      closeButton.style.cssText = `
        position: absolute;
        top: 20px;
        right: 20px;
        width: 40px;
        height: 40px;
        border: none;
        background: rgba(255, 255, 255, 0.9);
        color: #000;
        font-size: 24px;
        font-weight: bold;
        border-radius: 50%;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10;
      `;

      // Close function
      const closeModal = () => {
        modal.style.opacity = '0';
        setTimeout(() => {
          if (modal.parentNode) {
            modal.parentNode.removeChild(modal);
          }
          document.body.style.overflow = '';
        }, 300);
      };

      closeButton.addEventListener('click', closeModal);
      modal.addEventListener('click', (e) => {
        if (e.target === modal) closeModal();
      });

      document.addEventListener('keydown', function handleEscape(e) {
        if (e.key === 'Escape') {
          closeModal();
          document.removeEventListener('keydown', handleEscape);
        }
      });

      modal.appendChild(iframe);
      modal.appendChild(closeButton);
      document.body.appendChild(modal);
      document.body.style.overflow = 'hidden';

      // Trigger fade-in after a brief delay to ensure the DOM is updated
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          modal.style.opacity = '1';
        });
      });
    }

    closeModal() {
      const modal = document.getElementById(`social-videos-modal-${this.sectionId}`);
      const modalVideo = document.getElementById(`social-videos-modal-video-${this.sectionId}`);

      if (!modal) return;

      modal.classList.remove('active');
      if (modalVideo) {
        modalVideo.pause();
        modalVideo.src = '';
      }

      document.body.style.overflow = '';
    }

    /**
     * Utility Functions
     */
    pauseAllVideosInContainer(container) {
      if (!container) return;

      const videos = container.querySelectorAll('video');
      const cards = container.querySelectorAll('.social-video-card');

      videos.forEach(video => {
        if (!video.paused) {
          video.pause();
        }
      });

      cards.forEach(card => {
        card.classList.remove('playing');
      });
    }

    destroy() {
      // Cleanup observers
      if (this.sectionObserver) {
        this.sectionObserver.disconnect();
      }

      // Destroy Swipers
      Object.values(this.swiperInstances).forEach(swiper => {
        if (swiper && swiper.destroy) {
          swiper.destroy();
        }
      });
    }
  }

  /**
   * Initialize all Social Videos sections on page
   */
  function initSocialVideosSections() {
    const sections = document.querySelectorAll('.social-videos');
    sections.forEach(section => {
      if (!section.socialVideosInstance) {
        section.socialVideosInstance = new SocialVideosSection(section);
      }
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSocialVideosSections);
  } else {
    initSocialVideosSections();
  }

  // Shopify Section Editor support
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function(event) {
      const section = event.target.querySelector('.social-videos');
      if (section && !section.socialVideosInstance) {
        section.socialVideosInstance = new SocialVideosSection(section);
      }
    });

    document.addEventListener('shopify:section:unload', function(event) {
      const section = event.target.querySelector('.social-videos');
      if (section && section.socialVideosInstance) {
        section.socialVideosInstance.destroy();
        section.socialVideosInstance = null;
      }
    });
  }

})();
